import React, { Suspense, lazy, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Routes, Route, Link, useLocation, useParams } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import store from './store/store'; 
import ProtectedRoute from './components/ProtectedRoute';
import PageLayout from './components/PageLayout';
import { NotificationsProvider } from './context/NotificationsContext';
import "./i18n";
import { UserProvider } from './context/UserContext';
import AdminRoute from './components/AdminRoute';
import { bootstrapAuth } from "./services/authBootstrap";
import { hydrateAuth } from "./store/slices/authSlice";
import { initLazyAnalytics } from "./services/analyticsService";
import { useTranslation } from "react-i18next";
import SeoHead from "./components/SeoHead";
import RouteSeo from "./components/RouteSeo";

window.__BUILD_ID__ = process.env.REACT_APP_BUILD_ID || "development";

const AddOriginalPage = lazy(() => import('./pages/AddOriginalPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const BookDetailPage = lazy(() => import('./pages/BookDetailPage'));
const BookEditPage = lazy(() => import('./pages/BookEditPage'));
const BookmarksPage = lazy(() => import('./pages/BookmarksPage'));
const BudgetPage = lazy(() => import('./pages/BudgetPage'));
const Catalog = lazy(() => import('./pages/Catalog'));
const CopyrightPage = lazy(() => import('./pages/CopyrightPage'));
const CreateBookPage = lazy(() => import('./pages/CreateBookPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const Home = lazy(() => import('./pages/Home'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const PersonalPage = lazy(() => import('./pages/PersonalPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ReaderPage = lazy(() => import('./pages/ReaderPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const TranslatorPage = lazy(() => import('./pages/TranslatorPage'));

const RouteLoadingFallback = () => (
  <div style={{ color: "#fff", minHeight: "100vh", padding: 24 }}>
    Загрузка…
  </div>
);

function PageMeta({ noindexOnSearch = false, ...props }) {
  const location = useLocation();
  return <RouteSeo fallback={<RouteLoadingFallback />} {...props} noindex={props.noindex || (noindexOnSearch && !!location.search)} />;
}

function EntityFallback({ titleKey }) {
  const location = useLocation();
  const { t } = useTranslation();
  return (
    <>
      {location.key !== "default" && (
        <SeoHead title={`${t(titleKey)} — Ranobe Life`} robots="noindex,nofollow" />
      )}
      <RouteLoadingFallback />
    </>
  );
}

function EntityRoute({ titleKey, children }) {
  return <Suspense fallback={<EntityFallback titleKey={titleKey} />}>{children}</Suspense>;
}

function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-3xl px-4 py-12 text-white">
      <h1 className="text-2xl font-bold">{t("routeSeo.notFound")}</h1>
      <Link className="mt-4 inline-block text-[#F86240]" to="/">{t("home")}</Link>
    </div>
  );
}

function NotFoundRoute() {
  return <PageMeta titleKey="routeSeo.notFound" noindex><NotFoundPage /></PageMeta>;
}

function LegacyReaderRoute() {
  const { bookId, chapterId } = useParams();
  if (!/^\d+$/.test(bookId || "") || !/^\d+$/.test(chapterId || "")) {
    return <NotFoundRoute />;
  }
  return <EntityRoute titleKey="routeSeo.chapterLoading"><ReaderPage /></EntityRoute>;
}

function BootstrapGate({ children }) {
  const ready = useSelector((state) => state.auth.isResolved);

  useEffect(() => {
    let alive = true;
    const sessionVersion = store.getState().auth.sessionVersion;
    const handleSessionEnded = () => {
      store.dispatch(hydrateAuth({ authenticated: false, me: null }));
    };
    window.addEventListener("auth:session-ended", handleSessionEnded);
    (async () => {
      const { authenticated, me } = await bootstrapAuth();
      if (alive && store.getState().auth.sessionVersion === sessionVersion) {
        store.dispatch(hydrateAuth({ authenticated, me }));
      }
    })();
    return () => {
      alive = false;
      window.removeEventListener("auth:session-ended", handleSessionEnded);
    };
  }, []);

  if (!ready) return <div style={{ color: "#fff", padding: 24 }}>Загрузка…</div>;
  return children;
}


const root = ReactDOM.createRoot(document.getElementById('root'));
initLazyAnalytics();
root.render(
    <Provider store={store}> 
      <BrowserRouter>
        <BootstrapGate>
          <UserProvider>
            <NotificationsProvider>
              <PageLayout>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<PageMeta title="Ranobe-LIFE" descriptionKey="homeSeo.description" canonicalPath="/"><Home /></PageMeta>} />
                    <Route path="/create-new-book" element={<PageMeta titleKey="createBook" noindex><ProtectedRoute><CreateBookPage /></ProtectedRoute></PageMeta>} />
                    <Route path="/admin/:userId" element={<PageMeta titleKey="routeSeo.admin" noindex><AdminRoute><AdminPage /></AdminRoute></PageMeta>} />
                    <Route path="/budget/:userId" element={<PageMeta titleKey="routeSeo.budget" noindex><AdminRoute><BudgetPage /></AdminRoute></PageMeta>} />
                    <Route path="/book/:slug" element={<EntityRoute titleKey="routeSeo.bookLoading"><BookDetailPage /></EntityRoute>} />
                    <Route path="/add-original" element={<PageMeta titleKey="title.addOriginalText" noindex><ProtectedRoute><AddOriginalPage /></ProtectedRoute></PageMeta>} />
                    <Route path="/translator/:bookId/:chapterId" element={<PageMeta titleKey="routeSeo.translator" noindex><ProtectedRoute><TranslatorPage /></ProtectedRoute></PageMeta>} />
                    <Route path="/translator/book/:bookSlug/:chapterKey" element={<PageMeta titleKey="routeSeo.translator" noindex><ProtectedRoute><TranslatorPage /></ProtectedRoute></PageMeta>} />
                    <Route path="/reset-password" element={<PageMeta titleKey="resetPassword" noindex><ResetPasswordPage /></PageMeta>} />
                    <Route path="/:bookId/:chapterId" element={<LegacyReaderRoute />} />
                    <Route path="/reader/book/:bookSlug/chapter/:chapterNumber" element={<EntityRoute titleKey="routeSeo.chapterLoading"><ReaderPage /></EntityRoute>} />
                    <Route path="/profile" element={<PageMeta titleKey="routeSeo.myProfile" noindex><ProtectedRoute><PersonalPage /></ProtectedRoute></PageMeta>} />
                    <Route path="/profile/:userId" element={<PageMeta titleKey="routeSeo.userProfile" noindex><PersonalPage /></PageMeta>} />
                    <Route path="/profile/:userId/collection/:collectionId" element={<PageMeta titleKey="routeSeo.collection" noindex><PersonalPage /></PageMeta>} />
                    <Route path="/feedback" element={<PageMeta titleKey="feedback" descriptionKey="routeSeo.feedbackDescription" canonicalPath="/feedback"><FeedbackPage /></PageMeta>} />
                    <Route path="/copyright" element={<PageMeta titleKey="copyright.title" descriptionKey="copyright.intro" canonicalPath="/copyright"><CopyrightPage /></PageMeta>} />
                    <Route path="/terms" element={<PageMeta titleKey="terms.title" descriptionKey="terms.intro" canonicalPath="/terms"><TermsPage /></PageMeta>} />
                    <Route path="/privacy" element={<PageMeta titleKey="privacy.title" descriptionKey="privacy.intro" canonicalPath="/privacy"><PrivacyPolicyPage /></PageMeta>} />
                    <Route path="/book-edit/:bookId" element={<PageMeta titleKey="editBook" noindex><ProtectedRoute><BookEditPage /></ProtectedRoute></PageMeta>} />
                    <Route path="/notifications" element={<PageMeta titleKey="notifications" noindex><ProtectedRoute><NotificationsPage /></ProtectedRoute></PageMeta>} />
                    <Route path="/catalog" element={<PageMeta titleKey="catalogSeo.title" fullTitle descriptionKey="catalogSeo.description" canonicalPath="/catalog" noindexOnSearch><Catalog /></PageMeta>} />
                    <Route path="/bookmarks" element={<PageMeta titleKey="bookmarks" noindex><ProtectedRoute><BookmarksPage /></ProtectedRoute></PageMeta>} />
                    <Route path="/messages" element={<PageMeta titleKey="messages" noindex><ProtectedRoute><MessagesPage /></ProtectedRoute></PageMeta>} />
                    <Route path="*" element={<NotFoundRoute />} />
                  </Routes>
                </Suspense>
              </PageLayout>
            </NotificationsProvider>
          </UserProvider>
        </BootstrapGate>
      </BrowserRouter>
    </Provider>
);

reportWebVitals();
